import { useMemo } from 'react';
import { FIRM_TOTAL_LOAN_VALUE_REFERENCE } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/UseWaterfallFundSummarySheetConfig.constants';
import { inputSheetAfterCellsChanged } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/InputSheetRowConfig/inputSheetAfterCellsChanged';
import { buildTotalFundInvestmentsExpression } from 'pages/Waterfall/utilities';
import { UseCreateInputSheetProps } from './useCreateInputSheet.props';
import { SpreadsheetConfig } from '../../../../../components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import InputSheetParser from '../InputSheetParser/InputSheetParser';
import inputSheetRowConfig from '../InputSheetRowConfig/inputSheetRowConfig';

const useCreateInputSheet = ({
  exitDate,
  capTableId,
  equityValue,
  enterpriseValue,
  firmOptions,
  childrenCapTables,
  parentCapTables,
  fundInvestmentsSheetsNamesRef,
  plusCashIsFromFinancials,
  lessDebtIsFromFinancials,
  plusCash,
  lessDebt,
  isAdditionalCurrencySelected,
  exchangeRate,
  isExchangeRateOverridden,
  exchangeRateInputCurrency,
  inputPolicy,
  hasFirmTotalLoanValueCell,
}: UseCreateInputSheetProps) => {
  // Firm Total Exit Proceeds covers the whole firm position, so fund-held loans
  // — reported on their own Fund Summary column — join the securities total.
  const fundInvestmentsExpression = buildTotalFundInvestmentsExpression(fundInvestmentsSheetsNamesRef.current);
  let exitProceedsExpression = fundInvestmentsExpression;
  if (hasFirmTotalLoanValueCell) {
    exitProceedsExpression = fundInvestmentsExpression
      ? `${fundInvestmentsExpression} + ${FIRM_TOTAL_LOAN_VALUE_REFERENCE}`
      : `=${FIRM_TOTAL_LOAN_VALUE_REFERENCE}`;
  }
  const inputSheetConfig = useMemo(() => {
    if (!exitDate) {
      return null; // Ensure all required fields are provided
    }
    return new SpreadsheetConfig({
      rowConfig: inputSheetRowConfig({
        exitDate,
        capTableId,
        equityValue,
        firmOptions,
        plusCashIsFromFinancials,
        lessDebtIsFromFinancials,
        plusCash,
        lessDebt,
        enterpriseValue,
        childrenCapTables,
        parentCapTables,
        exitProceedsExpression,
        isAdditionalCurrencySelected,
        exchangeRate,
        isExchangeRateOverridden,
        exchangeRateInputCurrency,
        inputPolicy,
      }),
      //  if currency and additional currency is different, display 3 columns, last one for the additional currency
      columns: isAdditionalCurrencySelected ? [{}, {}, {}] : [{}, {}],
      parser: InputSheetParser,
      afterCellChanged: inputSheetAfterCellsChanged,
      reverseParser: () => ({}) as unknown as SpreadsheetConfig['reverseParser'], // no reverse parsing needed for this page
      showTitlesColumn: false,
      colTitleRow: 0,
      name: 'InputSheet',
      tableTerms: {} as SpreadsheetConfig['tableTerms'],
    });
  }, [
    exitDate,
    capTableId,
    equityValue,
    firmOptions,
    plusCash,
    lessDebt,
    enterpriseValue,
    childrenCapTables,
    parentCapTables,
    exitProceedsExpression,
    isAdditionalCurrencySelected,
    plusCashIsFromFinancials,
    lessDebtIsFromFinancials,
    exchangeRate,
    isExchangeRateOverridden,
    exchangeRateInputCurrency,
    inputPolicy,
  ]);
  return inputSheetConfig;
};

export default useCreateInputSheet;
